import React, { useContext } from 'react';
import produce from 'immer';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { ExperienceVariant } from '@/webapi/use-experience-api';
import { DeviceType } from '@/utils/definitions';
import {
  InlineVariantsLayoutAllocationInput,
  InlineVariantsLayoutAllocationWrapper,
} from '@/features/editor/widgets/shared/inline-variants-layout/shared';
import {
  EditorContext,
  EditorContextProps,
} from '@/features/editor/context/editor-context';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { formatAllocation } from '@/features/editor/widgets/targeting/shared';
import { InlineVariantsLayoutVariantName } from '@/features/editor/widgets/shared/inline-variants-layout/variant-name';
import { Flexbox } from '@/components/flex';

export interface InlineVariantsLayoutVariationProps {
  variant: ExperienceVariant;
  device: DeviceType;
  _changeAllocation?: (variant: ExperienceVariant) => void;
  publishedChance?: number;
  _variants?: ExperienceVariant[];
  disableNameEdit?: boolean;
  onRenameVariant?: (variantId: string, name: string) => void;
  bg?: string;
  variantDescription?: React.ReactNode;
  variantContent?: React.ReactNode;
  variantAction?: React.ReactNode;
  disableActiveStyles?: boolean;
  baselineVariantId?: string | null;
  setBaselineVariantId?: (id: string | null) => void;
  updateEnabled?: boolean;
}

export function InlineVariantsLayoutVariation({
  variant,
  device,
  _changeAllocation,
  publishedChance,
  _variants,
  disableNameEdit,
  onRenameVariant,
  bg,
  variantDescription,
  variantContent,
  variantAction,
  disableActiveStyles,
  baselineVariantId,
  setBaselineVariantId,
  updateEnabled,
}: InlineVariantsLayoutVariationProps) {
  const edtCtx = useContext(EditorContext);
  const variants =
    _variants || edtCtx?.experienceState?.currentExperience?.variants;
  const changeAllocation =
    _changeAllocation || edtCtx?.experienceState?.changeAllocation;
  const isActive = isActiveVariant(edtCtx, variant);

  const onTextChanged = (ev) => {
    const maxChance = getMaxChance(variants, variant);
    let value = parseAllocation(ev.target.value);
    if (value > maxChance) {
      value = maxChance;
    } else if (value < 0) {
      value = 0;
    }
    const updated = produce(variant, (draft) => {
      draft.chance = value;
      if (typeof _changeAllocation !== `undefined`) {
        draft.publishedChance = value;
      }
    });
    ev.target.value = formatAllocation(value);
    changeAllocation(updated);
  };

  return (
    <Wrapper
      device={device}
      isActive={!disableActiveStyles ? isActive : false}
      bg={bg}
      className="inline-variant-box-wrapper"
    >
      <InlineVariantsLayoutVariantName
        device={device}
        defaultValue={variant.name}
        variantId={variant.id}
        _variants={_variants}
        isActive={!disableActiveStyles ? isActive : false}
        bg={bg}
        disableNameEdit={disableNameEdit}
        onRenameVariant={onRenameVariant}
        footer={variantDescription}
        baselineVariantId={baselineVariantId}
        setBaselineVariantId={setBaselineVariantId}
        updateEnabled={updateEnabled}
        chance={variant.chance || 0}
      />
      <OkIcon isActive={!disableActiveStyles ? isActive : false} />
      <Flexbox direction="row" gap="1.5rem" align="center">
        {variantAction}
        <InlineVariantsLayoutAllocationInput
          device={device}
          onFocus={(e) => e.target.select()}
          onChange={onTextChanged}
          defaultValue={formatAllocation(
            publishedChance || (variant.chance ? variant.chance : 0),
          )}
        />
      </Flexbox>
      {variantContent && (
        <Flexbox direction="column" width="100%" className="extra-content">
          {variantContent}
        </Flexbox>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(InlineVariantsLayoutAllocationWrapper)`
  && {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas:
      'name allocation'
      'content content';

    && > *:nth-child(1) {
      grid-area: name;
    }

    && > *:nth-child(3) {
      grid-area: allocation;
    }

    && > .extra-content {
      grid-area: content;
    }

    .variant-name-wrapper {
      width: 100%;
    }
  }
`;

function OkIcon({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <OkWrapper className="inline-variant-selected-checkmark">
      <StaticImage
        src="../../../../../assets/approve-blue.svg"
        alt="edit"
        formats={[`webp`, `png`]}
        placeholder="none"
        loading="eager"
        height={20}
      />
    </OkWrapper>
  ) : null;
}

const OkWrapper = styled.div`
  width: 20px;
  position: absolute;
  left: 99%;
  top: 78%;
`;

function isActiveVariant(
  edtCtx: EditorContextProps,
  variant: ExperienceVariant,
) {
  const isMultiVariantEnabled = useFeatureBit(FeatureBit.MULTIVARIANT);
  return (
    isMultiVariantEnabled &&
    edtCtx?.experienceState?.currentExperience?.activeVariantId ===
      variant.id &&
    edtCtx?.experienceState?.currentExperience?.variants?.length > 1
  );
}

function getMaxChance(
  variants: ExperienceVariant[],
  variant: ExperienceVariant,
) {
  if (variants.length > 1) {
    return 100;
  }
  return (
    100 -
    variants
      .filter((v) => v.id !== variant.id)
      .reduce((p, c) => p + c.chance, 0)
  );
}

function parseAllocation(percent: string): number {
  try {
    return parseInt(percent.replaceAll(`%`, ``), 10);
  } catch (ignored) {
    return 0;
  }
}
